import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const { initiateLogin, verifyOtp, isAuthenticated, isLoading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('NG'); // Default to Nigeria
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [kycInfo, setKycInfo] = useState<{
    hasSharedKyc: boolean;
    kycStatus: string;
    kycRequirements: any;
  } | null>(null);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await initiateLogin(email, countryCode);
      setKycInfo(result);
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await verifyOtp(email, code);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4">
      <div className="w-full max-w-md p-6 md:p-8 space-y-6 swap-card rounded-2xl shadow-2xl">
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="/assets/pouchLogo.png"
              alt="PouchPay Logo"
              className="w-12 h-12 object-contain"
            />
            <h1 className="text-2xl md:text-3xl font-bold text-white">PouchPay</h1>
          </div>
          <p className="text-sm md:text-base text-slate-400 mt-2">
            {step === 'email' ? 'Sign in or create an account' : 'Enter verification code'}
          </p>
          {step === 'email' && (
            <p className="text-xs text-slate-500 mt-1">
              New users will automatically get an account created
            </p>
          )}
        </div>

        {error && (
          <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl">
            {error}
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleSendOtp} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-slate-300 mb-2">
                Country
              </label>
              <select
                id="country"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                required
                disabled={isLoading}
              >
                <option value="NG">Nigeria</option>
                <option value="GH">Ghana</option>
                <option value="KE">Kenya</option>
                <option value="ZA">South Africa</option>
                <option value="UG">Uganda</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="relative z-10">
                {isLoading ? 'Sending...' : 'Send verification code'}
              </span>
            </button>

            <p className="text-xs text-center text-slate-500 mt-3">
              By continuing, you agree to PouchPay's Terms of Service and Privacy Policy
            </p>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            {kycInfo && !kycInfo.hasSharedKyc && (
              <div className="p-4 text-sm bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <p className="font-semibold text-blue-400">New Account</p>
                <p className="text-slate-300 mt-1">
                  After verification, you'll need to complete KYC verification to use our services.
                </p>
              </div>
            )}

            {kycInfo && kycInfo.hasSharedKyc && (
              <div className="p-4 text-sm bg-green-500/10 border border-green-500/30 rounded-xl">
                <p className="font-semibold text-green-400">Welcome back!</p>
                <p className="text-slate-300 mt-1">
                  Your KYC is verified. You can continue using all services.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="code" className="block text-sm font-medium text-slate-300 mb-2">
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-4 py-3 bg-[var(--bg-3)] border border-[var(--border-primary)] rounded-xl text-white text-center text-2xl tracking-widest placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="000000"
                required
                disabled={isLoading}
                maxLength={6}
              />
              <p className="text-sm text-slate-400 mt-2">
                We sent a verification code to {email}
              </p>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className="swap-button w-full py-4 rounded-2xl text-white font-bold text-lg shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="relative z-10">
                  {isLoading ? 'Verifying...' : 'Verify'}
                </span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                }}
                className="w-full py-2 px-4 text-sm text-slate-400 hover:text-white transition-colors"
              >
                Use a different email
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
